import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Bell, Trophy, XCircle, PartyPopper, Check, Flag, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { VictoryShareCard } from "@/components/VictoryShareCard";

const ICONS = {
  bet_won: Trophy,
  bet_lost: XCircle,
  welcome: PartyPopper,
  champion_won: Trophy,
  champion_lost: XCircle,
  stage_settled: Trophy,
  podium: Trophy,
  fanion_upgrade: Flag,
};

const TINTS = {
  bet_won: "bg-[color:var(--palm)]/15 text-[color:var(--palm)]",
  champion_won: "bg-[color:var(--sunshine)]/25 text-amber-700",
  bet_lost: "bg-slate-100 text-slate-500",
  champion_lost: "bg-slate-100 text-slate-500",
  welcome: "bg-[color:var(--coral)]/15 text-[color:var(--coral)]",
  stage_settled: "bg-[color:var(--palm)]/15 text-[color:var(--palm)]",
  podium: "bg-[color:var(--sunshine)]/25 text-amber-700",
  fanion_upgrade: "bg-[color:var(--coral)]/15 text-[color:var(--coral)]",
};

const SHAREABLE = new Set(["podium", "fanion_upgrade", "champion_won"]);

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [shareCtx, setShareCtx] = useState(null);
  const { user } = useAuth();

  const load = async () => {
    const { data } = await api.get("/notifications");
    setItems(data);
  };

  useEffect(() => {
    load();
  }, []);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    await load();
  };

  const unread = items.filter((i) => !i.read).length;

  const openShare = (n) => {
    const variant = n.type === "fanion_upgrade" ? "fanion" : "podium";
    setShareCtx({
      variant,
      title: n.title || "Belle course !",
      subtitle: n.message || "",
      footer: n.type === "podium" ? "Podium sur le Tour des Yoles Rondes 2026" : "Fanion débloqué — Ma Petite Yole",
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-[color:var(--deep)]">
            Alertes
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            {unread > 0 ? `${unread} non lue${unread > 1 ? "s" : ""}` : "Tu es à jour."}
          </p>
        </div>
        {unread > 0 && (
          <Button
            onClick={markAll}
            data-testid="mark-all-read"
            variant="outline"
            className="rounded-full h-9 text-xs font-bold"
          >
            <Check className="h-3.5 w-3.5 mr-1" /> Tout marquer lu
          </Button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center">
          <Bell className="h-8 w-8 mx-auto text-slate-300 mb-3" />
          <div className="font-display font-bold text-lg text-[color:var(--deep)]">Aucune notification</div>
          <p className="text-sm text-slate-500 mt-1">Place ton premier pari pour lancer les alertes !</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100">
          {items.map((n, i) => {
            const Icon = ICONS[n.type] || Bell;
            const tint = TINTS[n.type] || "bg-slate-100 text-slate-500";
            return (
              <motion.div
                key={n.id}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                data-testid={`notif-${n.id}`}
                className={`flex items-start gap-3 px-5 py-4 ${!n.read ? "bg-[color:var(--sunshine)]/5" : ""}`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${tint}`}>
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm text-[color:var(--deep)] flex items-center gap-2">
                    {n.title}
                    {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--coral)]" />}
                  </div>
                  <div className="text-sm text-slate-500 mt-0.5">{n.message}</div>
                  <div className="text-[10px] font-mono text-slate-400 mt-1">
                    {new Date(n.created_at).toLocaleString("fr-FR")}
                  </div>
                  {SHAREABLE.has(n.type) && (
                    <Button
                      onClick={() => openShare(n)}
                      variant="outline"
                      size="sm"
                      data-testid={`share-${n.id}`}
                      className="mt-2 h-8 rounded-full text-[11px] font-bold border-[color:var(--sunshine)] text-[color:var(--deep)] hover:bg-[color:var(--sunshine)]/20"
                    >
                      <Share2 className="h-3 w-3 mr-1" /> Partager ma victoire
                    </Button>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {shareCtx && (
        <VictoryShareCard
          open={!!shareCtx}
          onClose={() => setShareCtx(null)}
          user={user}
          variant={shareCtx.variant}
          title={shareCtx.title}
          subtitle={shareCtx.subtitle}
          footer={shareCtx.footer}
        />
      )}
    </div>
  );
}
